import React, { useState, useEffect } from 'react';
import useNotification from '../../hooks/useNotification';
import { useAuth } from '../../hooks/useAuth';
import InfoModal from '../ui/InfoModal';
import Icon from '../AppIcon';

/**
 * Component to handle and display profile picture approval/rejection notifications
 * Listens for profile picture related notifications and displays them to the user
 */
const ProfilePictureNotification = () => {
  const { notifications, markAsRead } = useNotification();
  const { refreshUserProfile } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [processedNotifications, setProcessedNotifications] = useState(new Set());

  useEffect(() => {
    const isDevelopment = import.meta.env?.MODE === 'development';
    
    if (isDevelopment) {
      console.log('[ProfilePictureNotification] All notifications:', notifications);
    }

    // Check for new profile picture notifications
    // Match multiple possible type formats
    const profilePictureNotifs = notifications.filter(n => {
      const type = n.type?.toLowerCase() || '';
      const isProfilePictureNotif = type.includes('profile_picture') || 
                                     type.includes('profilepicture');
      if (isDevelopment) {
        console.log('[ProfilePictureNotification] Checking notification:', { 
          type: n.type,
          is_read: n.is_read,
          unread: n.unread,
          isProfilePictureNotif
        });
      }
      return isProfilePictureNotif;
    });

    if (isDevelopment) {
      console.log('[ProfilePictureNotification] Filtered profile_picture notifications:', profilePictureNotifs);
    }

    // Show the most recent unread notification (check both unread and !is_read for compatibility)
    const unreadNotif = profilePictureNotifs.find(n => n.unread || !n.is_read);
    if (isDevelopment) {
      console.log('[ProfilePictureNotification] Unread notification found:', unreadNotif);
    }

    if (unreadNotif && !showModal && !processedNotifications.has(unreadNotif.id)) {
      console.log('[ProfilePictureNotification] Showing modal for notification:', unreadNotif);
      setCurrentNotification(unreadNotif);
      setShowModal(true);
      setProcessedNotifications(prev => new Set(prev).add(unreadNotif.id));
      
      // Refresh user profile to get the updated picture and status
      if (refreshUserProfile) {
        refreshUserProfile();
      }
    }
  }, [notifications, showModal, processedNotifications]);

  const handleClose = () => {
    console.log('[ProfilePictureNotification] Closing modal');
    if (currentNotification?.id) {
      markAsRead(currentNotification.id);
    }
    setShowModal(false);
  };

  const handleButtonClick = () => {
    const isApproved = currentNotification?.type?.toLowerCase().includes('approved');
    if (isApproved) {
      window.location.href = '/va-profile';
    } else {
      window.location.href = '/va-profile-settings';
    }
    handleClose();
  };

  if (!currentNotification) {
    return null;
  }

  const isApproved = currentNotification.type?.toLowerCase().includes('approved');
  const modalType = isApproved ? 'success' : 'warning';
  const title = isApproved 
    ? '✅ Picture Approved!' 
    : '⚠️ Picture Rejected';

  const isDevelopment = import.meta.env?.MODE === 'development';
  if (isDevelopment) {
    console.log('[ProfilePictureNotification] Rendering modal:', {
      isApproved,
      title,
      message: currentNotification.message
    });
  }

  return (
    <InfoModal
      title={title}
      message={currentNotification.message}
      buttonLabel={isApproved ? 'View Profile' : 'Upload New Photo'}
      buttonAction={handleButtonClick}
      type={modalType}
      onClose={handleClose}
    />
  );
};

export default ProfilePictureNotification;
